<template>
  <div>
    <!-- 搜索条件区域：固定宽度，紧凑布局 -->
    <div class="search-container">
      <el-form :inline="true" :model="search" @submit.native.prevent>
        <el-form-item label="工程型号" style="margin-right: 20px;">
          <el-input
              v-model="search.projectId"
              placeholder="请输入工程型号"
              clearable
              style="width: 200px;"
              @keyup.enter.native="handleSearch">
          </el-input>
        </el-form-item>

        <!-- 新增：排查(排除)字段输入框，默认值为 "测试" -->
        <el-form-item label="排除型号" style="margin-right: 20px;">
          <el-tooltip class="item" effect="dark" content="支持多个排除词，用逗号隔开" placement="top">
            <el-input
                v-model="search.excludeKeyword"
                placeholder="例如: 测试,demo"
                clearable
                style="width: 200px;"
                @keyup.enter.native="handleSearch">
            </el-input>
          </el-tooltip>
        </el-form-item>

        <el-form-item label="时间范围" style="margin-right: 20px;">
          <el-date-picker
              v-model="search.date"
              type="daterange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              style="width: 350px;">
          </el-date-picker>
        </el-form-item>

        <el-form-item>
          <el-button type="primary" @click="handleSearch">查询</el-button>
          <el-button @click="resetSearch">重置</el-button>
        </el-form-item>
      </el-form>
    </div>

    <!-- 固定高度的表格容器 -->
    <div class="result-table-content">
      <el-table :data="tableData" :header-cell-style="{ color: 'black' }" style="width: 100%" v-loading="loading">
        <el-table-column type="index" label="序号" width="80" align="center"></el-table-column>

        <el-table-column prop="project_id" label="工程型号" width="180" align="center"></el-table-column>

        <el-table-column label="文件路径" min-width="240" align="center" show-overflow-tooltip>
          <template #default="scope">
            <div class="file-path">{{ scope.row.root_key }}</div>
          </template>
        </el-table-column>

        <el-table-column prop="created_at" label="创建时间" width="180" align="center">
          <template #default="scope">
            <div>{{ formatTimestamp(scope.row.created_at) }}</div>
          </template>
        </el-table-column>

        <el-table-column prop="updated_at" label="更新时间" width="180" align="center">
          <template #default="scope">
            <div>{{ formatTimestamp(scope.row.updated_at) }}</div>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="120" align="center" fixed="right">
          <template v-slot="scope">
            <el-button type="primary" size="small" @click="goToResultsPage(scope.row)">查看</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50, 100]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          background
          :total="total"
      >
      </el-pagination>
    </div>
  </div>
</template>

<script>
// API 引入
import { getProjects } from "@/api/file_info_sync";
import { ReplayByProjectId, syncProjectFile } from "@/components/file_process_js/main_process";

// 辅助函数：获取当天的 Date 对象数组
const getTodayRange = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);

  const end = new Date();
  end.setHours(23, 59, 59, 999);

  return [start, end];
};

export default {
  data() {
    return {
      search: {
        projectId: '',
        excludeKeyword: '测试', // 增加排除字段，默认值为 "测试"
        date: getTodayRange(),
      },
      tableData: [],
      currentPage: 1,
      pageSize: 10,
      total: 0,
      loading: false,
    };
  },
  methods: {
    handleSearch() {
      this.currentPage = 1;
      this.fetchProjectsData();
    },

    resetSearch() {
      // 重置时保留默认的"测试"排除条件
      this.search = {
        projectId: '',
        excludeKeyword: '测试',
        date: getTodayRange(),
      };
      this.currentPage = 1;
      this.pageSize = 10;
      this.fetchProjectsData();
    },

    handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
      this.fetchProjectsData();
    },

    handleCurrentChange(page) {
      this.currentPage = page;
      this.fetchProjectsData();
    },

    async fetchProjectsData() {
      this.loading = true;
      try {
        let start_time = 0;
        let end_time = 0;

        if (this.search.date && this.search.date.length === 2) {
          start_time = Math.floor(this.search.date[0].getTime() / 1000);
          end_time = Math.floor(this.search.date[1].getTime() / 1000) + 86400;
        }

        const skip = (this.currentPage - 1) * this.pageSize;
        const limit = this.pageSize;

        // 将前端填写的字符串按逗号分割成数组，过滤掉空串，兼容中英文逗号
        let excludeList = [];
        if (this.search.excludeKeyword) {
          excludeList = this.search.excludeKeyword
              .split(/[,，]/)
              .map(item => item.trim())
              .filter(item => item !== "");
        }

        // 调用接口，传入 excludeList
        const res = await getProjects(
            start_time,
            end_time,
            skip,
            limit,
            this.search.projectId,
            excludeList
        );

        if (res && res.res === "ok") {
          this.tableData = Array.isArray(res.data) ? res.data : [];
          this.total = res.count || 0;
        } else {
          this.tableData = [];
          this.total = 0;
        }

      } catch (error) {
        console.error("获取工程列表失败:", error);
        this.$message.error("获取数据失败");
      } finally {
        this.loading = false;
      }
    },

    async goToResultsPage(row) {
      try {
        this.$store.commit("setCommonObjList", []);
        this.$store.commit("setGerberObjList", []);
        this.$store.commit("setReadOnly", true);
        this.$store.commit("clearPollingList");

        await ReplayByProjectId(row.project_id);

        this.$router.push({name: "results"});
      } catch (error) {
        console.error("跳转详情失败:", error);
        this.$message.error("跳转失败，请重试");
      }
    },

    formatTimestamp(timestamp) {
      if (!timestamp || timestamp === 0) return "-";

      const date = new Date(timestamp * 1000);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    },
  },

  mounted() {
    this.fetchProjectsData();
  }
};
</script>

<style scoped>
.search-container {
  margin-bottom: 15px;
  padding: 10px 0;
}

.search-container .el-form-item {
  margin-bottom: 0;
}

.result-table-content {
  height: calc(100vh - 260px);
  overflow-y: auto;
  box-sizing: border-box;
}

.pagination-container {
  margin-top: 15px;
  float: right;
}
</style>